import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button as OpenXButton } from '@openx/components-core/lib/Button/Button.tsx';
import { CheckboxField } from '@openx/components-core/lib/CheckboxField/CheckboxField.tsx';
import type { DealInsightsChartInputApi } from '@openx/types/dealInsights.ts';
import { type ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealInsightsContext } from '../../../context/DealInsightsContext.tsx';
import {
  EXPORT_SECTION_CHART_KEYS,
  EXPORT_SECTION_OPTIONS,
  type ExportSectionId,
  getDefaultChartInputForKey,
} from '../constants.ts';

export const ExportSectionDropdown = () => {
  const { t } = useTranslation();
  const contextValue = useDealInsightsContext();
  const { dealInsights } = contextValue;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const isOpen = Boolean(anchorEl);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCheckboxChange = useCallback((e: ChangeEvent<HTMLInputElement>, value: string) => {
    if (e.target.checked) {
      setSelectedSections(prev => [...prev, value]);
    } else {
      setSelectedSections(prev => prev.filter(section => section !== value));
    }
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setIsGeneratingPdf(true);

      // Collect all chart keys needed for selected sections
      const allChartKeys = selectedSections.flatMap(
        section => EXPORT_SECTION_CHART_KEYS[section as ExportSectionId] || []
      );

      // Build chart inputs for all needed keys
      const chartInputs: Partial<DealInsightsChartInputApi> = {};
      for (const key of allChartKeys) {
        // @ts-expect-error - dynamic key assignment is safe here as keys come from DealInsightsChartInputApi
        chartInputs[key] = getDefaultChartInputForKey(key, contextValue.chartDateResolutions);
      }

      // Fetch data for all selected sections (from cache or via polling)
      const sectionData = await dealInsights.fetchDataForChartKeys(allChartKeys, chartInputs);

      // Generate PDF with fetched data (deferred: @react-pdf/renderer is only needed once the user exports)
      const { generateGeneralSectionPdf } = await import('../utils/generatePdf.tsx');
      // Generate PDF with fetched data
      await generateGeneralSectionPdf(sectionData, t, contextValue, undefined, selectedSections);
      handleClose();
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [dealInsights, t, contextValue, handleClose, selectedSections]);

  const handleClearSelection = useCallback(() => {
    setSelectedSections([]);
  }, []);

  const isEmpty = selectedSections.length === 0;
  const isAnyChartLoading = Object.values(dealInsights.chartsLoadingState).some(Boolean);

  return (
    <>
      <OpenXButton
        data-test="export-pdf-button"
        onClick={handleOpen}
        color="primary"
        variant="contained"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          paddingRight: 1,
        }}
      >
        {t('EXPORT PDF')}
        <KeyboardArrowDown
          data-test="popover-trigger-button-arrow"
          sx={{
            transition: 'transform 0.2s ease-in-out',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </OpenXButton>

      <Popover
        open={isOpen}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        data-test="export-sections-popover"
      >
        <Stack direction="column">
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingX: 2,
              paddingY: 1.5,
            }}
          >
            <Typography variant="h3" data-test="export-sections-popover-title">
              {t('Export Sections')}
            </Typography>
            <IconButton
              aria-label="Close"
              data-test="export-sections-popover-close-button"
              onClick={handleClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <Stack
            spacing={1.5}
            sx={{
              alignItems: 'flex-start',
              paddingX: 2,
              paddingY: 1.5,
            }}
          >
            {EXPORT_SECTION_OPTIONS.map(option => (
              <CheckboxField
                key={option.value}
                label={t(option.label)}
                checked={selectedSections.includes(option.value)}
                onChange={e => handleCheckboxChange(e, option.value)}
                data-test={`export-section-checkbox-${option.value}`}
              />
            ))}
          </Stack>

          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-end',
              paddingX: 1.75,
              paddingY: 1,
              gap: 1,
            }}
          >
            <Button
              data-test="export-sections-popover-export-button"
              color="primary"
              disabled={isEmpty || isGeneratingPdf || isAnyChartLoading}
              onMouseDown={e => e.preventDefault()}
              onClick={handleExport}
              variant="contained"
              startIcon={isGeneratingPdf ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('EXPORT')}
            </Button>

            <Button
              sx={{ color: 'text.secondary' }}
              data-test="export-sections-popover-clear-button"
              disabled={isEmpty}
              onMouseDown={e => e.preventDefault()}
              onClick={handleClearSelection}
              variant="text"
            >
              {t('CLEAR SELECTION')}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
